import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons';
import CheckboxContainer, { CHECKBOX_STATES } from '../Checkbox/Checkbox';
import treeCheckboxClasses from './TreeCheckbox.module.scss';
import Icon from '../Icon/Icon';

export const DEFAULT_CHECKED_STATE = CHECKBOX_STATES.Empty;

export const TreeContext = React.createContext();
export function TreeContextProvider(props) {
  const [checkedStatus, setCheckedStatus] = useState(props.checkedStatus || {});
  const [lastStatus, setLastStatus] = useState(props.checkedStatus || {});

  const [currentStatus] = useState({});
  const [currentItems, setCurrentItems] = useState();

  useEffect(() => {
    setCheckedStatus(props.checkedStatus);
  }, [props.checkedStatus]);

  const addStatus = (status, item) => {
    currentStatus[item.uuid] = status;
    if (props.onChange) {
      props.onChange(status, item);
    }
  };

  const saveStatus = (status, item) => {
    addStatus(status, item);

    if (item.centers) {
      item.centers.forEach((i) => saveStatus(status, i));
    }

    setLastStatus(checkedStatus);

    setCheckedStatus(currentStatus);
  };

  const getChechedStatus = (item) => {
    const isChecked =
      item && item.uuid && lastStatus && lastStatus[item.uuid]
        ? lastStatus[item.uuid]
        : DEFAULT_CHECKED_STATE;
    return item && item.uuid && checkedStatus[item.uuid]
      ? checkedStatus[item.uuid]
      : isChecked;
  };

  const value = useMemo(
    () => ({
      currentItems,
      setCurrentItems,
      checkedStatus,
      setCheckedStatus,
      saveStatus,
      getChechedStatus,
    }),
    [checkedStatus],
  );

  return (
    <TreeContext.Provider value={value} {...props}>
      {props.children}
    </TreeContext.Provider>
  );
}

export function BranchCheckbox({ item, indent, label, onChange }) {
  const [itemState] = useState(item);

  const { saveStatus, getChechedStatus } = useContext(TreeContext);

  const changeState = (e) => {
    let checkBoxState = DEFAULT_CHECKED_STATE;

    if (e.target.checked === true) checkBoxState = CHECKBOX_STATES.Checked;
    else if (e.target.checked === false) checkBoxState = CHECKBOX_STATES.Empty;

    if (onChange) onChange(checkBoxState, itemState);

    saveStatus(checkBoxState, itemState);
  };

  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <CheckboxContainer
        id={itemState.uuid}
        value={itemState.uuid}
        onChange={(e) => changeState(e)}
        customStyle={{ paddingLeft: indent }}
        label={label ? label(itemState) : itemState.name || itemState.code}
        checked={getChechedStatus(itemState)}
        isIndeterminate={
          getChechedStatus(itemState) === CHECKBOX_STATES.Indeterminate
        }
        onChangeContainer={handleOpen}
        suffix={
          itemState.centers && (
            <Icon
              icon={faAngleUp}
              className={
                isOpen
                  ? treeCheckboxClasses.iconExpanded
                  : treeCheckboxClasses.iconCollapsed
              }
            />
          )
        }
      />
      {itemState.centers ? (
        <TreeCheckboxNode
          parent={itemState}
          items={itemState.centers}
          indent={indent + 30}
          checked={getChechedStatus(itemState)}
          onCheckboxChange={onChange}
          className={[
            treeCheckboxClasses.hidden,
            isOpen ? treeCheckboxClasses.open : treeCheckboxClasses.collapse,
          ]}
        />
      ) : null}
    </>
  );
}

export function TreeCheckboxNode({
  className,
  items,
  indent,
  label,
  onCheckboxChange,
}) {
  const cn = cx(treeCheckboxClasses.container, className);
  const [itemsState, setItemsState] = useState(items);

  const { getChechedStatus, setCurrentItems } = useContext(TreeContext);

  useEffect(() => {
    setItemsState(items);
    setCurrentItems(items);
  }, [items]);

  const indentDefault = indent || 1;

  return (
    itemsState &&
    itemsState.map((item) => {
      return (
        <div key={item.uuid} className={cn}>
          <BranchCheckbox
            label={label}
            item={item}
            checked={getChechedStatus(item)}
            indent={indentDefault}
            onChange={onCheckboxChange}
          />
        </div>
      );
    })
  );
}

export default function TreeCheckbox({
  checkedStatus,
  items,
  label,
  indent,
  checked,
  onChange,
  onCheckboxChange,
}) {
  return (
    <TreeContextProvider checkedStatus={checkedStatus} onChange={onChange}>
      <TreeCheckboxNode
        label={label}
        items={items}
        checked={checked}
        indent={indent}
        onCheckboxChange={onCheckboxChange}
      />
    </TreeContextProvider>
  );
}
const stringOrArray = PropTypes.oneOfType([PropTypes.string, PropTypes.array]);

TreeContextProvider.propTypes = {
  children: PropTypes.node,
  onChange: PropTypes.func,
  checkedStatus: PropTypes.object,
};

TreeCheckbox.propTypes = {
  items: PropTypes.array,
  indent: PropTypes.number,
  checkedStatus: PropTypes.object,
  onChange: PropTypes.func,
  onCheckboxChange: PropTypes.func,
  label: PropTypes.func,
  checked: PropTypes.oneOf([
    CHECKBOX_STATES.Checked,
    CHECKBOX_STATES.Indeterminate,
    CHECKBOX_STATES.Empty,
  ]),
};

TreeCheckboxNode.propTypes = {
  items: PropTypes.array,
  parent: PropTypes.object,
  className: stringOrArray,
  label: PropTypes.func,
  indent: PropTypes.number,
  onCheckboxChange: PropTypes.func,
  checked: PropTypes.oneOf([
    CHECKBOX_STATES.Checked,
    CHECKBOX_STATES.Indeterminate,
    CHECKBOX_STATES.Empty,
  ]),
};

BranchCheckbox.propTypes = {
  item: PropTypes.object,
  label: PropTypes.func,
  indent: PropTypes.number,
  onChange: PropTypes.func,
};
